import React, { useState, useRef, useCallback } from 'react';
import { Upload, Copy, Check, X, Image as ImageIcon, Crop } from 'lucide-react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CropArea extends Area {
  // react-easy-crop uses pixelCrop
}

// Utility function to create image blob from crop area
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area
): Promise<Blob> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Set canvas size to match the cropped area
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Convert canvas to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
};

export const ImageUploadPage: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showCrop, setShowCrop] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [uploadedData, setUploadedData] = useState<{
    id: string;
    original_url: string;
    transform_url_template: string;
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [copied, setCopied] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size should be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setUploadedUrl(null);
    setUploadedData(null);
    setCopied(null);
    setCroppedImageBlob(null);
    
    // Create image preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) {
        setImageSrc(result);
        setShowCrop(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setImageLoaded(false); // Reset to false, will be set to true by onImageLoaded
      } else {
        toast.error('Failed to load image');
      }
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((croppedArea: CropArea, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onImageLoaded = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleCropComplete = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedImageBlob(blob);
      setShowCrop(false);
      toast.success('Image cropped successfully!');
    } catch (error: any) {
      toast.error('Failed to crop image');
      console.error('Crop error:', error);
    }
  };

  const handleSkipCrop = () => {
    setShowCrop(false);
    setCroppedImageBlob(null);
    setImageLoaded(false);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select an image first');
      return;
    }

    // Use cropped image if available, otherwise use original
    const fileToUpload = croppedImageBlob 
      ? new File([croppedImageBlob], selectedFile.name, { type: 'image/jpeg' })
      : selectedFile;

    try {
      setUploading(true);
      setUploadProgress(0);

      const result = await apiService.uploadToImageCloudinary(fileToUpload, (progress) => {
        setUploadProgress(progress);
      });

      // Extract file extension from the original filename
      const fileExt = selectedFile.name.split('.').pop()?.toLowerCase() || 'jpg';
      
      // Generate transformation URLs
      const transformUrls = {
        w_1200: `https://img.bozapp.in/t/w_1200/${result.id}.${fileExt}`,
        w_800: `https://img.bozapp.in/t/w_800/${result.id}.${fileExt}`,
        w_400: `https://img.bozapp.in/t/w_400/${result.id}.${fileExt}`,
      };

      setUploadedData({
        ...result,
        transform_url_template: result.transform_url_template,
      });
      setUploadedUrl(result.original_url);
      toast.success('Image uploaded successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCopyUrl = async (url: string) => {
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      toast.success('URL copied to clipboard!');
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setImageSrc(null);
    setImageLoaded(false);
    setShowCrop(false);
    setCroppedImageBlob(null);
    setCroppedAreaPixels(null);
    setUploadedUrl(null);
    setUploadedData(null);
    setUploadProgress(0);
    setCopied(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <h1 className="text-2xl font-semibold text-gray-900">Image Upload</h1>
        <p className="text-sm text-gray-600 mt-1">Upload images and get their URLs</p>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto">
          {/* Crop Modal */}
          {showCrop && imageSrc && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4 overflow-y-auto"
              onClick={(e) => {
                // Close modal if clicking on backdrop
                if (e.target === e.currentTarget) {
                  handleSkipCrop();
                }
              }}
            >
              <div 
                className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col my-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                    <Crop className="w-5 h-5" />
                    <span>Crop Image</span>
                  </h2>
                  <button
                    onClick={handleSkipCrop}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Skip cropping"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div 
                  className="relative" 
                  style={{ 
                    height: '60vh',
                    minHeight: '400px',
                    backgroundColor: '#000',
                    width: '100%'
                  }}
                >
                  {imageSrc ? (
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={undefined}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                      onImageLoaded={onImageLoaded}
                      style={{
                        containerStyle: {
                          width: '100%',
                          height: '100%',
                          position: 'relative',
                        },
                        cropAreaStyle: {
                          border: '2px solid #fff',
                        },
                        mediaStyle: {
                          objectFit: 'contain',
                        },
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-white">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                        <p>Loading image...</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zoom: {Math.round(zoom * 100)}%
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleSkipCrop}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                      Skip Cropping
                    </button>
                    <button
                      onClick={handleCropComplete}
                      className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                    >
                      Apply Crop
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* File Selection Area */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col items-center justify-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="image-upload-input"
              />
              <label
                htmlFor="image-upload-input"
                className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <ImageIcon className="w-12 h-12 text-gray-400 mb-4" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to select</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                </div>
              </label>

              {selectedFile && (
                <div className="mt-4 w-full">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <ImageIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleClear}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Clear selection"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Upload Button */}
          {selectedFile && !uploadedUrl && !showCrop && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              {croppedImageBlob && (
                <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                  <p className="text-sm text-primary-700 flex items-center space-x-2">
                    <Crop className="w-4 h-4" />
                    <span>Image has been cropped. Ready to upload.</span>
                  </p>
                </div>
              )}
              
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Uploading... {Math.round(uploadProgress)}%</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    <span>Upload Image</span>
                  </>
                )}
              </button>

              {uploading && uploadProgress > 0 && (
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Uploaded URL Display */}
          {uploadedUrl && uploadedData && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Uploaded Image URLs</h2>
              
              {/* Preview */}
              <div className="mb-6">
                <img
                  src={uploadedUrl}
                  alt="Uploaded"
                  className="w-full max-w-md mx-auto rounded-lg border border-gray-200"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>

              {/* Original URL */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Original URL
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={uploadedUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => handleCopyUrl(uploadedUrl)}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                    aria-label="Copy URL"
                  >
                    {copied === uploadedUrl ? (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Transformation URLs */}
              <div className="space-y-4 mb-6">
                <h3 className="text-md font-semibold text-gray-800">Transformation URLs</h3>
                
                {/* w_1200 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Width 1200px
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={`https://img.bozapp.in/t/w_1200/${uploadedData.id}.${selectedFile?.name.split('.').pop()?.toLowerCase() || 'jpg'}`}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => handleCopyUrl(`https://img.bozapp.in/t/w_1200/${uploadedData.id}.${selectedFile?.name.split('.').pop()?.toLowerCase() || 'jpg'}`)}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                    >
                      {copied === `https://img.bozapp.in/t/w_1200/${uploadedData.id}.${selectedFile?.name.split('.').pop()?.toLowerCase() || 'jpg'}` ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* w_800 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Width 800px
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={`https://img.bozapp.in/t/w_800/${uploadedData.id}.${selectedFile?.name.split('.').pop()?.toLowerCase() || 'jpg'}`}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => handleCopyUrl(`https://img.bozapp.in/t/w_800/${uploadedData.id}.${selectedFile?.name.split('.').pop()?.toLowerCase() || 'jpg'}`)}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                    >
                      {copied === `https://img.bozapp.in/t/w_800/${uploadedData.id}.${selectedFile?.name.split('.').pop()?.toLowerCase() || 'jpg'}` ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* w_400 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Width 400px
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={`https://img.bozapp.in/t/w_400/${uploadedData.id}.${selectedFile?.name.split('.').pop()?.toLowerCase() || 'jpg'}`}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => handleCopyUrl(`https://img.bozapp.in/t/w_400/${uploadedData.id}.${selectedFile?.name.split('.').pop()?.toLowerCase() || 'jpg'}`)}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                    >
                      {copied === `https://img.bozapp.in/t/w_400/${uploadedData.id}.${selectedFile?.name.split('.').pop()?.toLowerCase() || 'jpg'}` ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Clear Button */}
              <button
                onClick={handleClear}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Upload Another Image
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

